// Zustand store for the overlay's view state. Keeps pipeline-card states
// keyed by PipelineId so the stack component can iterate a fixed order.

import { create } from "zustand";
import type {
  FinalGuess,
  GameMode,
  LlmGuessPayload,
  MetaPayload,
  OcrPayload,
  PipelineId,
  PipelineResultEvent,
  PipelineStartedEvent,
} from "../types/contract";

export type CardState =
  | { status: "idle" }
  | { status: "running"; startedAtMs: number }
  | {
      status: "complete";
      startedAtMs: number;
      latencyMs: number;
      /// Per-model pinpoint payload, populated for ai_* pipelines.
      /// Optional so non-LLM pipelines (reverse_image) keep the same
      /// CardState shape without a stub field.
      guess?: LlmGuessPayload;
      /// Populated for the `ocr` pipeline — extracted text + place
      /// name candidate.
      ocr?: OcrPayload;
      /// Populated for the `meta` pipeline — region-narrowing signals.
      meta?: MetaPayload;
    }
  | {
      status: "failed";
      startedAtMs: number;
      latencyMs: number;
      errorCategory: string;
      errorMessage: string;
    }
  | { status: "cancelled" }
  | { status: "skipped" };

export type ToastKind = "ok" | "warn" | "error";
export interface ToastState {
  message: string;
  kind: ToastKind;
  // Monotonic id so consecutive identical messages still re-trigger the
  // auto-dismiss timer when the same toast is shown twice in a row.
  id: number;
}

export interface StreamSafety {
  streamSafe: boolean;
  build: number | null;
  minBuild: number;
  /// Whether `SetWindowDisplayAffinity` returned success at startup.
  /// Constant for the lifetime of the process. Kept distinct from the
  /// live runtime signal below.
  setCallSucceeded: boolean;
  /// Whether the process is currently inside a Terminal Services / RDP
  /// / Quick Assist remote session. Polled, can flip when the user
  /// attaches/detaches an RDP session mid-run. The RDP capture pipeline
  /// bypasses `WDA_EXCLUDEFROMCAPTURE` regardless of the stored flag,
  /// so this is the load-bearing runtime signal for "the overlay would
  /// leak into a screencast right now".
  inRemoteSession: boolean;
}

export interface ReverseImageStaleness {
  yandexOk: boolean;
  lensOk: boolean;
  bingOk: boolean;
}

export interface HotkeyFailure {
  binding: string;
  spec: string;
  error: string;
}

export interface StoreState {
  roundNumber: number;
  capturedAtMs: number | null;
  thumbnailPath: string | null;
  pipelineCards: Partial<Record<PipelineId, CardState>>;
  finalGuess: FinalGuess | null;
  opacity: number;
  toast: ToastState | null;
  streamSafety: StreamSafety | null;
  reverseImageStaleness: ReverseImageStaleness | null;
  /// Hotkey-registration state, mirroring the typed wire payload.
  /// Counters are `number` (not `number | null`) — the Rust emitter
  /// unconditionally populates them, so the null path was dead. The
  /// whole slice is `null` until the first capability event lands;
  /// that's the loading-state indicator. The setter takes the typed
  /// payload object instead of three positional args so a future
  /// field addition (e.g. `skipped`) doesn't silently drop.
  hotkeyState: HotkeyRegistrationState | null;
  /// Currently-selected GeoGuessr mode. Drives FinalGuessFooter
  /// rendering (country-only modes hide lat/lng + map; show country
  /// label only). Initialised to classic_location and overwritten
  /// at mount by `getGameMode()`.
  gameMode: GameMode;
  setGameMode: (m: GameMode) => void;
  /// The correlation_id of the most recent round Atlas dispatched.
  /// Updated whenever a pipeline://started event lands. The
  /// TruthRecorder uses it to attach a recorded truth to the right
  /// row in the stats DB.
  currentRoundId: string | null;
  /// Whether the StatsPanel modal is currently visible. Toggled
  /// from the TopBar's Stats button; closes via the panel's own
  /// Close button or programmatic dismiss.
  statsOpen: boolean;
  setStatsOpen: (open: boolean) => void;
  settingsOpen: boolean;
  setSettingsOpen: (open: boolean) => void;
  /// Pipeline ids the user has disabled. Mirrored from
  /// `[llm].disabled_pipelines` in user.toml; populated at mount
  /// via `get_settings` and refreshed whenever the SettingsPanel
  /// auto-saves a toggle change. PipelineStack hides cards for
  /// any pipeline in this set so the UI matches the actual
  /// dispatch behaviour — disabled slots don't fire and
  /// shouldn't render an empty "idle" card.
  disabledPipelines: string[];
  setDisabledPipelines: (ids: string[]) => void;
  applyStarted: (e: PipelineStartedEvent) => void;
  applyResult: (e: PipelineResultEvent) => void;
  setFinal: (g: FinalGuess | null) => void;
  setRoundNumber: (n: number) => void;
  setThumbnail: (p: string | null, t: number | null) => void;
  setOpacity: (o: number) => void;
  showToast: (message: string, kind?: ToastKind) => void;
  dismissToast: () => void;
  setStreamSafety: (s: StreamSafety) => void;
  setReverseImageStaleness: (s: ReverseImageStaleness) => void;
  setHotkeyState: (s: HotkeyRegistrationState) => void;
}

export interface HotkeyRegistrationState {
  failures: HotkeyFailure[];
  registered: number;
  total: number;
}

export const useStore = create<StoreState>((set) => ({
  roundNumber: 1,
  capturedAtMs: null,
  thumbnailPath: null,
  pipelineCards: {},
  finalGuess: null,
  opacity: 0.92,
  applyStarted: (e) =>
    set((s) => ({
      currentRoundId: e.id,
      pipelineCards: {
        ...s.pipelineCards,
        [e.pipeline]: { status: "running", startedAtMs: e.started_at_ms },
      },
    })),
  applyResult: (e) =>
    set((s) => ({
      pipelineCards: {
        ...s.pipelineCards,
        [e.pipeline]:
          e.status === "complete"
            ? {
                status: "complete",
                startedAtMs: e.started_at_ms,
                latencyMs: e.latency_ms,
                guess: e.guess,
                ocr: e.ocr,
                meta: e.meta,
              }
            : e.status === "failed"
              ? {
                  status: "failed",
                  startedAtMs: e.started_at_ms,
                  latencyMs: e.latency_ms,
                  errorCategory: e.error_category ?? "internal",
                  errorMessage: e.error_message ?? "",
                }
              : e.status === "cancelled"
                ? { status: "cancelled" }
                : { status: "skipped" },
      },
    })),
  setFinal: (g) => set({ finalGuess: g }),
  setRoundNumber: (n) => set({ roundNumber: n }),
  setThumbnail: (p, t) => set({ thumbnailPath: p, capturedAtMs: t }),
  setOpacity: (o) => set({ opacity: o }),
  toast: null,
  showToast: (message, kind = "ok") =>
    set((s) => ({
      toast: { message, kind, id: (s.toast?.id ?? 0) + 1 },
    })),
  dismissToast: () => set({ toast: null }),
  streamSafety: null,
  setStreamSafety: (s) => set({ streamSafety: s }),
  reverseImageStaleness: null,
  setReverseImageStaleness: (s) => set({ reverseImageStaleness: s }),
  hotkeyState: null,
  setHotkeyState: (s) => set({ hotkeyState: s }),
  gameMode: "classic_location",
  setGameMode: (m) => set({ gameMode: m }),
  currentRoundId: null,
  statsOpen: false,
  setStatsOpen: (open) => set({ statsOpen: open }),
  settingsOpen: false,
  setSettingsOpen: (open) => set({ settingsOpen: open }),
  disabledPipelines: [],
  setDisabledPipelines: (ids) => set({ disabledPipelines: ids }),
}));
